import LMSDispatcher from "../LMSDispatcher";

import { IApi, Api } from "../../utilities/Api";
import { ApiRequest } from "../../utilities/ApiRequest";
import * as ACTIONCONTANTS from "./ActionConstants";
import {
  IPropertyDetailViewData,
  PropertyDetailViewData,
} from "../../models/property/PropertyViewData";
import IPropertyFormData from "../../models/property/PropertyFormData";
import i18next from "i18next";

class PropertyActions {
  private readonly api: IApi;

  constructor() {
    this.api = new Api(ACTIONCONTANTS.SAMPLE_ENDPOINT);
  }

  async getById(id: string) {
    try {
      const request = new ApiRequest(
        "/property/" + id,
        "",
        null,
        i18next.t("Property not found.")
      );
      const result = await this.api.get(request);
      const payload = this.convertResponseData(result.data);
      LMSDispatcher.dispatch({
        type: ACTIONCONTANTS.PROPERTY_GET_BY_ID,
        payload: {
          propertyData: payload,
        },
      });
    } catch (error) {
      throw error;
    }
  }

  async getSelected(id: string[]) {
    try {
      const request = new ApiRequest(
        "/Property/selected?ids=" + id.join("&ids="),
        "",
        null,
        i18next.t("Properties not found.")
      );
      const result = await this.api.get(request);
      const payload = this.convertResponseListData(result.data);

      LMSDispatcher.dispatch({
        type: ACTIONCONTANTS.PROPERTY_GET_ALL_SELECTED_ID,
        payload: {
          data: payload,
        },
      });
    } catch (error) {
      throw error;
    }
  }

  async getSelectedCalculated(id: string[]) {
    try {
      const request = new ApiRequest(
        "/Property/calculatedproperties",
        "",
        id,
        i18next.t("Properties not found.")
      );
      const result = await this.api.post(request);
      const payload = this.convertResponseListData(result.data);

      LMSDispatcher.dispatch({
        type: ACTIONCONTANTS.PROPERTY_GET_ALL_SELECTED_CALCULATED_ID,
        payload: {
          data: payload,
        },
      });
    } catch (error) {
      throw error;
    }
  }

  async getAll(
    page: number,
    pageSize: number,
    sort: string,
    order: string,
    filter: string
  ) {
    try {
      const request = new ApiRequest(
        `/Property/${page}/${pageSize}/${sort}/${order}/${filter}`,
        "",
        null,
        i18next.t("Unable to fetch properties.")
      );
      const result = await this.api.get(request);
      const payload = this.convertResponseListData(result.data);
      LMSDispatcher.dispatch({
        type: ACTIONCONTANTS.PROPERTY_GET_ALL,
        payload: {
          propertyDataList: payload,
        },
      });
    } catch (error) {
      throw error;
    }
  }

  async getAllPropertiesWithInstruments(
    page: number,
    pageSize: number,
    sort: string,
    order: string,
    filter: string
  ) {
    try {
      const request = new ApiRequest(
        `/Property/PropertiesWithInstruments/${page}/${pageSize}/${sort}/${order}/${filter}`,
        "",
        null,
        i18next.t("Unable to fetch properties.")
      );
      const result = await this.api.get(request);
      const payload = this.convertResponseListInsDirData(result.data);
      LMSDispatcher.dispatch({
        type: ACTIONCONTANTS.PROPERTY_INS_DIR_GET_ALL,
        payload: {
          propertyInsDirDataList: payload,
        },
      });
    } catch (error) {
      throw error;
    }
  }

  async getAllMeasuredProperties() {
    try {
      const request = new ApiRequest(
        "/Property/measuredProperties",
        "",
        null,
        i18next.t("Unable to fetch measured properties.")
      );
      const result = await this.api.get(request);
      const payload = this.convertResponseListData(result.data);
      LMSDispatcher.dispatch({
        type: ACTIONCONTANTS.PROPERTY_GET_ALL,
        payload: {
          propertyDataList: payload,
        },
      });
    } catch (error) {
      throw error.message;
    }
  }

  async getByType(id: string) {
    try {
      const request = new ApiRequest(
        "/Property/" + id + "/type",
        "",
        null,
        i18next.t("Unable to fetch properties.")
      );
      const result = await this.api.get(request);
      const payload = this.convertResponseListData(result.data);
      LMSDispatcher.dispatch({
        type: ACTIONCONTANTS.PROPERTY_GET_ALL,
        payload: {
          propertyDataList: payload,
        },
      });
    } catch (error) {
      throw error;
    }
  }

  async post(data: IPropertyFormData): Promise<void> {
    try {
      const payload: any = this.preparePostPayload(data);

      const request = new ApiRequest(
        "/property/",
        data.requestId,
        payload,
        i18next.t("Property created.")
      );
      const result = await this.api.post(request);

      LMSDispatcher.dispatch({
        type: ACTIONCONTANTS.PROPERTY_SUBMIT,
        payload: {
          propertyId: result.data,
          message: result.message,
        },
      });
    } catch (error) {
      throw error;
    }
  }

  async put(data: IPropertyFormData): Promise<void> {
    try {
      const payload: any = this.preparePutPayload(data);

      const request = new ApiRequest(
        "/property/",
        data.requestId,
        payload,
        i18next.t("Property updated.")
      );
      const result = await this.api.put(request);

      LMSDispatcher.dispatch({
        type: ACTIONCONTANTS.PROPERTY_SUBMIT,
        payload: {
          propertyId: data.id,
          message: result.message,
        },
      });
    } catch (error) {
      throw error;
    }
  }

  async delete(id: string, requestId: string): Promise<void> {
    try {
      const request = new ApiRequest(
        "/property/" + id,
        requestId,
        undefined,
        i18next.t("Property deleted.")
      );
      const result = await this.api.delete(request);

      LMSDispatcher.dispatch({
        type: ACTIONCONTANTS.PROPERTY_DELETE,
        payload: {
          reload: true,
          message: result.message,
        },
      });
    } catch (error) {
      throw error;
    }
  }

  async checkFormula(formula: string): Promise<boolean> {
    try {
      const params = new URLSearchParams([["formula", formula]]);
      const request = new ApiRequest(
        "/CalculatedProperty/checkformula?" + params,
        undefined,
        i18next.t("Invalid Formula.")
      );
      const result = await this.api.post(request);
      return result.data as boolean;
    } catch (error) {
      return false;
    }
  }

  private preparePostPayload(data: IPropertyFormData): any {
    const formulaVariables: any[] = [];
    data.formulaVariables.forEach((x) => {
      formulaVariables.push({
        propertyId: x.propertyId,
        propertyAttributeId: x.propertyAttributeId,
        variable: x.variable,
      });
    });
    return {
      customId: Number(data.customId),
      name: data.name,
      typeId: data.typeId,
      unit: data.unit,
      decimals: Number(data.decimals),
      description: data.description,
      formula: data.formula,
      formulaVariables: formulaVariables,
    };
  }

  private preparePutPayload(data: IPropertyFormData): any {
    const formulaVariables: any[] = [];
    data.formulaVariables.forEach((x) => {
      formulaVariables.push({
        propertyId: x.propertyId,
        propertyAttributeId: x.propertyAttributeId,
        variable: x.variable,
      });
    });
    return {
      id: data.id,
      customId: Number(data.customId),
      name: data.name,
      typeId: data.typeId,
      unit: data.unit,
      decimals: Number(data.decimals),
      description: data.description,
      formula: data.formula,
      formulaVariables,
    };
  }

  private convertResponseInsDirData(data: any): IPropertyDetailViewData {
    return new PropertyDetailViewData(
      data.id,
      data.name,
      data.description,
      data.customId,
      data.type.name,
      data.type.id,
      data.unit,
      data.decimals,
      data.formula,
      data.formulaDetails,
      data.instrumentDetails,
      data.directions
    );
  }

  private convertResponseListInsDirData(data: any) {
    const result: IPropertyDetailViewData[] = [];
    if (data.contents) {
      data.contents.forEach((element: any) => {
        result.push(this.convertResponseInsDirData(element));
      });
    } else {
      data.forEach((element: any) => {
        result.push(this.convertResponseInsDirData(element));
      });
    }
    const tempResult = {
      result: data.contents ? result : result,
      currentPage: data.contents ? data.currentPage : 0,
      totalPages: data.contents ? data.totalPages : 0,
      totalRecords: data.contents ? data.totalRecords : 0,
    };
    return tempResult;
  }

  private convertResponseData(data: any): IPropertyDetailViewData {
    return new PropertyDetailViewData(
      data.id,
      data.name,
      data.description,
      data.customId,
      data.type.name,
      data.type.id,
      data.unit,
      data.decimals,
      data.formula,
      data.formulaDetails,
      [],
      []
    );
  }

  private convertResponseListData(data: any) {
    const result: IPropertyDetailViewData[] = [];
    if (data.contents) {
      data.contents.forEach((element: any) => {
        result.push(this.convertResponseData(element));
      });
    } else {
      data.forEach((element: any) => {
        result.push(this.convertResponseData(element));
      });
    }
    const tempResult = {
      result: data.contents ? result : result,
      currentPage: data.contents ? data.currentPage : 0,
      totalPages: data.contents ? data.totalPages : 0,
      totalRecords: data.contents ? data.totalRecords : 0,
    };
    return tempResult;
  }
}

const propertyActions = new PropertyActions();
export default propertyActions;
